// ServicesReview.jsx
import React, { useState, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import assetData from './refinery-assets.json';

const firebaseConfig = {
  apiKey: "AIzaSyBALO-oNqvxmRhTSx9S54A5PIilTrxVOYM",
  authDomain: "refinery-desk-dashboard.firebaseapp.com",
  projectId: "refinery-desk-dashboard",
  storageBucket: "refinery-desk-dashboard.firebasestorage.app",
  messagingSenderId: "28035012838",
  appId: "1:28035012838:web:eb80f171a10371cdbec327"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export default function ServicesReview({ userId, selectedCustomer, onBack }) {
  const [columns, setColumns] = useState(4);
  const [successMessage, setSuccessMessage] = useState('');

  // ====== helpers ======
  const norm = (s) => (s || '').toString().trim().toLowerCase();
  const isCustomerType = (t) => {
    const x = norm(t);
    return x === 'customer' || x === 'influencer' || x === 'kol';
  };
  const toCustomerCanonical = (t) => (norm(t) === 'kol' ? 'influencer' : norm(t));

  // ====== build images from items (review-tags) ======
  const images = (assetData.services_review || []).map((item) => {
    // รับได้ทั้ง array และ string (คั่น comma)
    let rawTags = item['review-tags'];
    if (typeof rawTags === 'string') {
      rawTags = rawTags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (!Array.isArray(rawTags)) rawTags = [];

    // normalize tags สำหรับ logic
    const normalized = rawTags.map((t) => toCustomerCanonical(t));

    return {
      label: item.name,
      url: item.url,
      caption: item.textAfter || '',
      tagsRaw: rawTags,          // ไว้ทำ label ถ้าต้องการ
      tags: normalized,          // ใช้กรองจริง
    };
  });

  // ====== derive tag list from items ======
  // สร้างรายการแท็ก (label=รูปแบบที่ใส่มา, value=normalize)
  const tagMap = new Map(); // value -> label (เก็บ label แรกที่เจอ)
  (assetData.services_review || []).forEach((item) => {
    let raw = item['review-tags'];
    if (typeof raw === 'string') {
      raw = raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (!Array.isArray(raw)) return;
    raw.forEach((tag) => {
      const val = toCustomerCanonical(tag);
      if (!tagMap.has(val)) tagMap.set(val, tag);
    });
  });

  // แยกเป็น 2 กลุ่ม
  const allTags = [...tagMap.entries()].map(([value, label]) => ({ value, label }));
  const customerTypeTags = allTags
    .filter(({ value }) => isCustomerType(value))
    .map(({ value }) => ({
      value,
      label: value === 'influencer' ? 'Influencer (KOL)' : 'Real Customer',
    }));

  // ✅ Services เรียง A→Z (ตาม label, case-insensitive)
  const serviceTags = allTags
    .filter(({ value }) => !isCustomerType(value))
    .sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase(), undefined, { sensitivity: 'base' })
    );

  // ====== filter states ======
  const [selectedTags, setSelectedTags] = useState([]); // array<string> value (normalized)
  const [pendingTags, setPendingTags] = useState([]);   // array<string> value (normalized)
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // ====== scrolling ======
  const scrollRef = useRef(null);
  const getScrollableParent = (el) => {
    let node = el;
    while (node && node !== document.body) {
      const { overflowY } = window.getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return window;
  };
  const scrollToTop = () => {
    const target = getScrollableParent(scrollRef.current);
    if (target === window) window.scrollTo({ top: 0, behavior: 'smooth' });
    else target.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const title = 'Services Review';
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewCaption, setPreviewCaption] = useState('');

  // ====== LINE helpers ======
  const sendToLine = async (text) => {
    try {
      const res = await fetch('https://therefineryclinic.herokuapp.com/sendToLine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, message: text }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'ส่ง LINE ไม่สำเร็จ');
    } catch (err) {
      console.error('❌ LINE ERROR:', err);
      alert('❌ ไม่สามารถส่งข้อความผ่าน LINE ได้');
    }
  };

  const sendAndLogText = async (text) => {
    try {
      await sendToLine(text);
      const latestCustomerInfo = selectedCustomer || {};
      await addDoc(collection(db, 'messages'), {
        from: 'me',
        role: 'me',
        userId,
        type: 'text',
        customer: text,
        createdAt: serverTimestamp(),
        displayName: latestCustomerInfo.displayName || 'Customer',
        pictureUrl: latestCustomerInfo.pictureUrl || '',
      });
    } catch (err) {
      console.error('❌ เกิดข้อผิดพลาด:', err);
    }
  };

  const sendAndLogImage = async ({ imageUrl, label }) => {
    try {
      const res = await fetch('https://therefineryclinic.herokuapp.com/sendToLineImage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, imageUrl }),
      });
      if (!res.ok) throw new Error('ส่งไม่สำเร็จ');

      const latestCustomerInfo = selectedCustomer || {};
      await addDoc(collection(db, 'messages'), {
        from: 'me',
        role: 'me',
        userId,
        type: 'image',
        imageUrl,
        createdAt: serverTimestamp(),
        displayName: latestCustomerInfo.displayName || 'Customer',
        pictureUrl: latestCustomerInfo.pictureUrl || '',
      });

      const message = `Successfully sent "${label}" from the ${title} section`;
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 1800);
    } catch (err) {
      console.error(err);
      alert('❌ ไม่สามารถส่งภาพได้');
    }
  };

  const sendWithCaption = async ({ imageUrl, label, caption }) => {
    try {
      const res = await fetch('https://therefineryclinic.herokuapp.com/sendToLineImage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, imageUrl }),
      });
      if (!res.ok) throw new Error('ส่งไม่สำเร็จ');

      const latestCustomerInfo = selectedCustomer || {};
      await addDoc(collection(db, 'messages'), {
        from: 'me',
        role: 'me',
        userId,
        type: 'image',
        imageUrl,
        createdAt: serverTimestamp(),
        displayName: latestCustomerInfo.displayName || 'Customer',
        pictureUrl: latestCustomerInfo.pictureUrl || '',
      });

      await sendAndLogText(caption);
      const message = `"${label}" with caption from the ${title} section has been sent successfully`;
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 1800);
    } catch (err) {
      console.error(err);
      alert('❌ ไม่สามารถส่งภาพ + ข้อความได้');
    }
  };

  // ====== grid cols ======
  const gridColsClass = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  }[columns] || 'md:grid-cols-4';

  // แยก selection ออกเป็น 2 กลุ่ม
  const selectedServiceVals = selectedTags.filter((t) => !isCustomerType(t));
  const selectedCustomerVals = selectedTags.filter((t) => isCustomerType(t));

  // helper: ถ้ากลุ่มว่าง = ผ่าน, ถ้ามีค่าให้ OR กัน
  const matchesGroupOR = (img, group) =>
    group.length === 0 || group.some((t) => img.tags.includes(t));

  // ====== filtering (AND logic) ======
  const displayedImages =
    selectedTags.length === 0
      ? []
      : images.filter(
        (img) =>
          matchesGroupOR(img, selectedServiceVals) &&
          matchesGroupOR(img, selectedCustomerVals)
      );

  // ====== modal handlers ======
  const openFilter = () => {
    setPendingTags(selectedTags);
    setIsFilterOpen(true);
  };
  const togglePendingTag = (value) => {
    const v = norm(value);
    setPendingTags((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );
  };
  const clearFilter = () => setPendingTags([]);
  const applyFilter = () => {
    setSelectedTags(pendingTags);
    setIsFilterOpen(false);
  };

  return (
    <div ref={scrollRef} className="relative max-w-screen-xl mx-auto px-4">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-3 items-center mt-[-2px] mb-4 gap-3">
        <div className="flex items-center gap-3 md:justify-start justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onBack}
            className="px-2 py-1 text-xs rounded-full border border-gray-300 text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
          >
            ← Back
          </button>
        </div>

        <div className="flex justify-center">
          <button
            onClick={openFilter}
            className="w-full max-w-xs md:max-w-md lg:max-w-lg px-4 py-2 text-sm rounded-full border border-gray-300 bg-white hover:bg-gray-50"
          >
            {selectedTags.length === 0
              ? 'Select Review Tags'
              : `Selected: ${selectedTags.map((v) => tagMap.get(v) || v).join(', ')}`}
          </button>
        </div>

        <div className="flex items-center gap-2 md:justify-end justify-center">
          {[4, 3, 2].map((col) => (
            <button
              key={col}
              onClick={() => setColumns(col)}
              className={`px-2 py-1 text-xs rounded-full border ${columns === col
                  ? 'bg-black text-white border-black'
                  : 'border-gray-300 text-gray-500 hover:bg-gray-100'
                }`}
            >
              {col} / row
            </button>
          ))}
        </div>
      </div>

      {/* Grid / placeholder */}
      {selectedTags.length === 0 ? (
        <div className="py-16 text-center text-gray-500">
          Use the <span className="font-medium">'Select Review Tags'</span> button to choose which reviews to display
        </div>
      ) : (
        <div className={`grid grid-cols-2 gap-6 ${gridColsClass}`}>
          {displayedImages.map((img, index) => {
            // คุมปุ่ม Caption
            const cap = typeof img.caption === 'string' ? img.caption.trim() : '';
            const hasCaption = cap !== '' && cap.toLowerCase() !== 'no-text';

            return (
              <div
                key={index}
                className="flex flex-col items-stretch p-4 rounded-2xl border shadow-sm bg-white h-full"
              >
                <div className="w-full aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] rounded-xl overflow-hidden bg-gray-50">
                  <img
                    src={img.url}
                    alt={img.label}
                    onClick={() => {
                      setPreviewImageUrl(img.url);
                      setPreviewCaption(img.caption || '');
                    }}
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="mt-4 text-center text-sm text-gray-800">{img.label}</div>

                <div className="mt-auto w-full flex flex-col items-center">
                  <button
                    onClick={() => sendAndLogImage({ imageUrl: img.url, label: img.label })}
                    className="mt-2 px-6 py-1.5 bg-gray-100 text-gray-800 rounded-full hover:bg-gray-200 transition text-sm"
                  >
                    Send
                  </button>

                  {hasCaption ? (
                    <button
                      onClick={() =>
                        sendWithCaption({ imageUrl: img.url, label: img.label, caption: img.caption })
                      }
                      className="mt-2 px-6 py-1.5 bg-black text-white rounded-full hover:opacity-80 transition text-sm"
                    >
                      + Caption
                    </button>
                  ) : (
                    <button
                      disabled
                      className="mt-2 px-6 py-1.5 rounded-full border border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed opacity-60 text-sm"
                      title="This image has no caption"
                    >
                      No Caption
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {successMessage && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white shadow-xl rounded-xl px-6 py-4 border border-gray-200 text-center max-w-sm w-full">
            <div className="text-sm text-gray-800 mb-3 whitespace-pre-line">{successMessage}</div>
            <button
              onClick={() => setSuccessMessage('')}
              className="text-xs px-3 py-1 border border-gray-300 rounded-full hover:bg-gray-100 transition text-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Preview lightbox */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setPreviewImageUrl(null);
            setPreviewCaption('');
          }}
        >
          <div className="pointer-events-none max-w-5xl w-full flex justify-center">
            <div
              className="pointer-events-auto relative inline-block"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={previewImageUrl}
                alt="Preview"
                className="max-h-[70vh] w-auto rounded-xl shadow-2xl"
              />
              {/* ✅ แสดง 'no caption' ถ้า no-text/ว่าง */}
              {(() => {
                const cap = (previewCaption || '').trim();
                const showNoCap = cap === '' || cap.toLowerCase() === 'no-text';
                return (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className={`absolute left-1/2 -translate-x-1/2 w-[100%] max-w-[22rem] max-h-28 overflow-y-auto rounded-xl shadow-lg px-3 py-2 text-[13px] leading-snug whitespace-pre-line border ${showNoCap
                        ? 'bg-gray-100 border-gray-300 text-gray-500 italic'
                        : 'bg-white/90 backdrop-blur border-gray-200 text-gray-900'
                      }`}
                  >
                    {showNoCap ? 'no caption' : cap}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div className="mt-6 flex justify-between items-center">
        <button
          onClick={onBack}
          className="px-2 py-1 text-xs rounded-full border border-gray-300 
             text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
        >
          ← Back
        </button>

        <button
          onClick={scrollToTop}
          className="px-2 py-1 text-xs rounded-full border border-gray-300 
             text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
        >
          ↑ Top
        </button>
      </div>

      {/* Modal — กว้างขึ้น + Services เป็น 2 คอลัมน์ */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-[92vw] md:w-[90vw] lg:w-[72vw] xl:w-[64vw] max-h-[82vh] rounded-2xl p-5 overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base md:text-lg font-semibold">Filter Reviews</h4>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="px-2 py-1 text-xs rounded-full border border-gray-300 text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
              >
                ✕ Close
              </button>
            </div>

            {/* Selected pill bar */}
            <div className="mb-4 flex flex-wrap gap-2">
              {pendingTags.length === 0 ? (
                <span className="text-xs text-gray-400">No tags selected</span>
              ) : (
                pendingTags.map((v) => (
                  <span
                    key={v}
                    className="text-xs px-2 py-1 rounded-full bg-gray-100 border border-gray-300 text-gray-700"
                  >
                    {tagMap.get(v) || v}
                  </span>
                ))
              )}
            </div>

            {/* Two columns */}
            <div className="grid grid-cols-1 md:grid-cols-[65%_30%] gap-8">
              {/* Column 1: Services (2 คอลัมน์ภายใน) */}
              <div>
                <div className="text-sm font-semibold mb-3">Filter by Services</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {serviceTags.map(({ value, label }) => {
                    const active = pendingTags.includes(value);
                    return (
                      <button
                        type="button"
                        key={value}
                        onClick={() => togglePendingTag(value)}
                        className={`text-left px-4 py-2 rounded-lg border transition ${active
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                          }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Column 2: Customer type (stack vertical) */}
              <div>
                <div className="text-sm font-semibold mb-3">Filter by Customer Type</div>
                <div className="flex flex-col gap-2">
                  {customerTypeTags.map(({ value, label }) => {
                    const active = pendingTags.includes(value);
                    return (
                      <button
                        type="button"
                        key={value}
                        onClick={() => togglePendingTag(value)}
                        className={`w-full text-left px-4 py-2 rounded-lg border transition ${active
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                          }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={clearFilter}
                className="px-3 py-2 text-xs md:text-sm rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Clear
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="px-4 py-2 text-xs md:text-sm rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyFilter}
                  className="px-4 py-2 text-xs md:text-sm rounded-full bg-black text-white hover:opacity-90"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
